import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import Spring from '@/components/Spring';
import TournamentCard from '@/components/TournamentCard';
import { useTournamentPagedList } from '@/hooks/useTournamentList';
import styles from './styles.module.scss';

interface MyTournamentsProps {
  onTournamentClick?: (id: number) => void;
}

const MAX_TOURNAMENTS = 6;
const ANIM_DURATION = 340;
const DRAG_THRESHOLD = 4;

const MyTournaments: React.FC<MyTournamentsProps> = ({ onTournamentClick }) => {
  const { t } = useTranslation('tournament');
  // Same list, order and cache family as the "my" tab: first page, status order.
  const { items: tournaments, isPending } = useTournamentPagedList('my', {
    page: 1,
    limit: MAX_TOURNAMENTS,
  });

  const trackRef = useRef<HTMLDivElement | null>(null);
  const animFrameRef = useRef<number | null>(null);
  const animTimerRef = useRef<number | null>(null);
  const [activeIdx, setActiveIdx] = useState(0);

  const getCards = useCallback(() => {
    const track = trackRef.current;
    if (!track) return [] as HTMLElement[];
    return Array.from(track.children) as HTMLElement[];
  }, []);

  const getPadLeft = useCallback(() => {
    const track = trackRef.current;
    if (!track) return 0;
    return parseFloat(getComputedStyle(track).paddingLeft) || 0;
  }, []);

  const updateActive = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const cards = getCards();
    if (cards.length === 0) return;
    const sl = track.scrollLeft;
    const padLeft = getPadLeft();
    let best = 0;
    let bestD = Infinity;
    cards.forEach((c, i) => {
      const d = Math.abs((c.offsetLeft - padLeft) - sl);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    });
    setActiveIdx(best);
  }, [getCards, getPadLeft]);

  const animateTo = useCallback((targetRaw: number) => {
    const track = trackRef.current;
    if (!track) return;
    const maxScroll = track.scrollWidth - track.clientWidth;
    const target = Math.min(Math.max(0, targetRaw), maxScroll);
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (animTimerRef.current) window.clearTimeout(animTimerRef.current);
    const start = track.scrollLeft;
    const dist = target - start;
    if (Math.abs(dist) < 1) {
      track.scrollLeft = target;
      updateActive();
      return;
    }
    const t0 = performance.now();
    const ease = (p: number) => 1 - Math.pow(1 - p, 3);
    const step = (now: number) => {
      const p = Math.min(1, (now - t0) / ANIM_DURATION);
      track.scrollLeft = start + dist * ease(p);
      if (p < 1) {
        animFrameRef.current = requestAnimationFrame(step);
      } else {
        animFrameRef.current = null;
        updateActive();
      }
    };
    animFrameRef.current = requestAnimationFrame(step);
    // Safety net: rAF can pause in backgrounded tabs / Telegram WebView; setTimeout still fires.
    animTimerRef.current = window.setTimeout(() => {
      if (Math.abs(track.scrollLeft - target) > 1) {
        track.scrollLeft = target;
        updateActive();
      }
    }, ANIM_DURATION + 90);
  }, [updateActive]);

  const scrollToCard = useCallback((idx: number) => {
    const track = trackRef.current;
    if (!track) return;
    const cards = getCards();
    if (!cards[idx]) return;
    animateTo(cards[idx].offsetLeft - getPadLeft());
  }, [animateTo, getCards, getPadLeft]);

  // Mouse drag-to-scroll. Touch uses native scrolling + snap.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    let down = false;
    let startX = 0;
    let startScroll = 0;
    let moved = false;

    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      down = true;
      moved = false;
      startX = e.clientX;
      startScroll = track.scrollLeft;
      track.classList.add(styles.isDragging);
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!down) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > DRAG_THRESHOLD) moved = true;
      track.scrollLeft = startScroll - dx;
    };
    const onPointerUp = () => {
      if (!down) return;
      down = false;
      track.classList.remove(styles.isDragging);
      // Settle to the nearest snap point.
      const cards = getCards();
      const padLeft = getPadLeft();
      let best = 0;
      let bestD = Infinity;
      cards.forEach((c, i) => {
        const d = Math.abs((c.offsetLeft - padLeft) - track.scrollLeft);
        if (d < bestD) {
          bestD = d;
          best = i;
        }
      });
      scrollToCard(best);
    };
    const onClickCapture = (e: MouseEvent) => {
      // Swallow click after a drag so navigation doesn't fire.
      if (moved) {
        e.stopPropagation();
        e.preventDefault();
      }
    };

    track.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    track.addEventListener('click', onClickCapture, true);

    return () => {
      track.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      track.removeEventListener('click', onClickCapture, true);
    };
  }, [getCards, getPadLeft, scrollToCard]);

  // Keep active dot in sync with native scroll + resize.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    track.addEventListener('scroll', updateActive, { passive: true });
    const ro = new ResizeObserver(updateActive);
    ro.observe(track);
    updateActive();
    return () => {
      track.removeEventListener('scroll', updateActive);
      ro.disconnect();
    };
  }, [updateActive, tournaments.length]);

  useEffect(() => () => {
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (animTimerRef.current) window.clearTimeout(animTimerRef.current);
  }, []);

  if (isPending && tournaments.length === 0) {
    return (
      <Spring className={styles.widget}>
        <div className={styles.head}>
          <h3 className={styles.title}>{t('myTournaments')}</h3>
        </div>
        <div className={styles.empty}>
          <p className={styles.emptyText}>{t('loadingTournaments')}</p>
        </div>
      </Spring>
    );
  }

  if (tournaments.length === 0) {
    return (
      <Spring className={styles.widget}>
        <div className={styles.head}>
          <h3 className={styles.title}>{t('myTournaments')}</h3>
        </div>
        <div className={styles.empty}>
          <p className={styles.emptyText}>{t('noTournamentsYet')}</p>
          <Link to="/tournaments" className={styles.joinBtn}>
            {t('browseTournaments')}
          </Link>
        </div>
      </Spring>
    );
  }

  return (
    <Spring className={styles.widget}>
      <div className={styles.head}>
        <h3 className={styles.title}>{t('myTournaments')}</h3>
        <Link to="/tournaments" className={styles.viewAll}>
          {t('viewAll')}
          <i className="icon-chevron-right" aria-hidden="true" />
        </Link>
      </div>

      <div className={styles.body}>
        <div className={styles.slider}>
          <div className={styles.track} ref={trackRef}>
            {tournaments.map((tournament) => (
              <TournamentCard
                key={tournament.id}
                tournament={tournament}
                onClick={() => onTournamentClick?.(tournament.id)}
                showUserStats
                showDetails={false}
                className={styles.slide}
              />
            ))}
          </div>
          {tournaments.length > 1 && (
            <div className={styles.dots}>
              {tournaments.map((tournament, i) => (
                <button
                  key={tournament.id}
                  type="button"
                  className={classNames(styles.dot, { [styles.dotActive]: i === activeIdx })}
                  aria-label={`Go to card ${i + 1}`}
                  onClick={() => scrollToCard(i)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </Spring>
  );
};

export default MyTournaments;
